import react, { useReducer, useEffect, useState } from "react"

import {
    Box,
    Button,
    Chip,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    FormControlLabel,
    Switch,
    TextField,
    Alert,
    MenuItem
} from "@mui/material"

import { createTheme, ThemeProvider } from "@mui/material/styles"

import * as moment from "moment"

import {
    DataGridPro,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    gridClasses,
    ptBR
} from "@mui/x-data-grid-pro"

import { ptBR as corePtBR } from "@mui/material/locale"

import ReceiptIcon from "@mui/icons-material/Receipt"
import InvoiceIcon from "@mui/icons-material/Description"
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import CloseIcon from "@mui/icons-material/Close"
import CancelIcon from "@mui/icons-material/Cancel"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"

import ConfirmationDialogCaptcha from "../Dialogs/ConfimationDialogCaptcha"
import Loading from "../Loading"

import { lockedReducer } from "../../utils/defaultReducer"

import useNotification from "../Notification"

import api from "../../api"

import "./styles.css"

const NFSE_CANCEL_REASONS = [
    { value: "1", label: "1 - Erro na emissão" },
    { value: "2", label: "2 - Serviço não prestado" },
    { value: "3", label: "3 - Erro de competência" },
    { value: "4", label: "4 - Duplicidade da nota" },
    { value: "9", label: "9 - Outros" },
]

const NFSE_CANCEL_REASON_HELP = {
    "1": "Erro na emissão: valor, CNPJ/CPF, imposto ou outro dado fiscal incorreto.",
    "2": "Serviço não prestado: a nota foi emitida, mas o serviço não chegou a ser executado.",
    "3": "Erro de competência: a nota foi emitida no mês ou período errado.",
    "4": "Duplicidade da nota: a mesma cobrança gerou duas notas para o mesmo serviço.",
    "9": "Outros: use esse motivo quando nenhum dos anteriores se aplicar. Nesse caso, descreva com clareza o ocorrido.",
}

const NFSE_CANCEL_REASON_DEFAULT_DESCRIPTION = {
    "1": "Erro na emissão",
    "2": "Servico nao prestado",
    "3": "Erro de competencia",
    "4": "Duplicidade da nota",
    "9": "",
}

const NFSE_CANCEL_DESCRIPTION_MIN = 15
const NFSE_CANCEL_DESCRIPTION_MAX = 255

const theme = createTheme({
    palette: {
        primary: { main: "#21446C" },
    },
}, ptBR, corePtBR)

const translate_status = {
    'CANCELLED': 'Cancelado'
}

const CustomToolbar = (gridClasses) => (
    <GridToolbarContainer className={gridClasses.toolbarContainer}>
        <Box className='toolbarLeft'>
            <GridToolbarColumnsButton size="small" />
            <GridToolbarFilterButton size="small" className={'filterControl'} />
        </Box>
    </GridToolbarContainer>
)

const getPayments = async (setState) => {
    setState({ loading: true })
    const res = await api.get("/api/payment_history/")
    if (res.status === 200) {
        setState({
            history: res.data,
            loading: false
        })
        return
    }
    setState({ loading: false })
}

const getPaymentStatus = (status, row) => {
    if (row.payed_at && row.payed_value >= row.total_value_number)
        return { color: "success", status: "Pago" }
    else if (row.payed_at && row.payed_value < row.total_value_number)
        return { color: "info", status: "Pago Parcial" }
    else if (status in translate_status)
        return { color: "error", status: translate_status[status] }
    else if ((new Date(row.due_at)) < (new Date()).setHours(0, 0, 0, 0))
        return { color: "warning", status: "Atrasado" }
    return { color: "error", status: 'Pendente' }
}

const historyColumns = (setParent) => {
    return [
        { field: "id", headerName: "ID", flex: 1 }, // Manter somente para testes. Remover depois.
        { field: "enterprise_name", headerName: "Empresa", flex: 1 },
        {
            field: "created_at", headerName: "Data da Cobrança", flex: 1,
            renderCell: (params) => (
                <Box>
                    {params.value
                        ? moment(params.value).format("DD/MM/YYYY").toString()
                        : ""
                    }
                </Box>
            )
        },
        {
            field: "monthly_value", headerName: "Mensalidade", flex: 1,
            renderCell: (params) => new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
            }).format(params.value)
        },
        {
            field: "monthly_exceeding", headerName: "Excedente", flex: 1,
            renderCell: (params) => new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
            }).format(params.value)
        },
        {
            field: "taxes", headerName: "ISS", flex: 1,
            renderCell: ({value}) => {
                return new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                }).format(value)
            }
        },
        {
            field: "total_value_number", headerName: "Valor Total", flex: 1,
            renderCell: ({value}) => 
                new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                }).format(value)
        },
        {
            field: "due_at", headerName: "Data do Vencimento", flex: 1,
            renderCell: (params) => (
                <Box>
                    {params.value
                        ? moment(params.value).format("DD/MM/YYYY").toString()
                        : ""
                    }
                </Box>
            )
        },
        {
            field: "status", headerName: "Status", flex: 1,
            renderCell: ({value, row}) => {
                const { color, status } = getPaymentStatus(value, row)
                return (
                    <Chip label={status} color={color} size="small" />
                )
            }
        },
        {
            field: "nfse_status", headerName: "Status da Nota", flex: 1,
            renderCell: ({ value }) => {
                const map = {
                    EMITIDA: { color: "success", label: "Emitida" },
                    PROCESSING: { color: "info", label: "Processando" },
                    ERRO: { color: "error", label: "Erro" },
                    CANCELADA: { color: "default", label: "Cancelada" },
                }

                const current = map[value] || { color: "default", label: value || "Sem nota" }

                return (
                    <Chip
                        label={current.label}
                        color={current.color}
                        size="small"
                    />
                )
            }
        },
        {
            field: "payed_at", headerName: "Data do Pagamento", flex: 1,
            renderCell: (params) => (
                <Box>
                    {params.value
                        ? moment(params.value).format("DD/MM/YYYY").toString()
                        : ""
                    }
                </Box>
            )
        },
        {
            field: "payed_value", headerName: "Valor Pago", flex: 1,
            renderCell: (params) => (params.value)
                ? new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                }).format(params.value)
                : ""
        },
        {
            field: "action", headerName: "Ações", flex: 1, filterable: false,
            renderCell: (params) => {
                const canEmitNfse = !["EMITIDA", "PROCESSING", "CANCELADA"].includes(params.row.nfse_status)
                const canCancelNfse = params.row.nfse_status === "EMITIDA"
                return (
                    <Box>
                        <Tooltip title="Ver boleto" placement="bottom">
                            <Button className="action-button"
                                onClick={() => (params.row.invoice)
                                    ? window.open(params.row.invoice)
                                    : useNotification(
                                        "Indisponível!",
                                        "O boleto não está disponível.",
                                        "info"
                                    )
                                }>
                                <InvoiceIcon />
                            </Button>
                        </Tooltip>
                        <Tooltip title="Reenviar boleto" placement="bottom">
                            <Button className="action-button"
                                onClick={() => setParent({ historyRow: params.row })}>
                                <ForwardToInboxIcon />
                            </Button>
                        </Tooltip>
                        <Tooltip title="Ver nota fiscal" placement="bottom">
                            <Button className="action-button"
                                onClick={() => (params.row.nfse)
                                    ? downloadNfse(params.row, setParent)
                                    : useNotification(
                                        "Indisponível!",
                                        "A nota fiscal não está disponível.",
                                        "info"
                                    )
                                }>
                                <ReceiptIcon />
                            </Button>
                        </Tooltip>
                        {canEmitNfse &&
                            <Tooltip title="Emitir nota fiscal" placement="bottom" disableInteractive>
                                <Button className="action-button"
                                    onClick={() => setParent({ createNfseRow: params.row })}>
                                    <ReceiptLongIcon />
                                </Button>
                            </Tooltip>
                        }
                        {canCancelNfse &&
                            <Tooltip title="Cancelar nota fiscal" placement="bottom" disableInteractive>
                                <Button className="action-button delete"
                                    onClick={() => setParent({ cancelNfseRow: params.row })}>
                                    <CancelIcon />
                                </Button>
                            </Tooltip>
                        }
                    </Box>
                )
            }
        }
    ]
}

const resendInvoice = async (historyRow, extraData, setParent) => {
    setParent({ loading: true })
    const res = await api.post("/api/payment_history/resend_invoice/", {
        history_row: historyRow,
        include_managers: extraData["includeManagers"],
        extra_emails: extraData["extraEmails"]
    })

    if (res.status === 200) {
        useNotification(
            "Sucesso!",
            "A cobrança foi reenviada com sucesso.",
            "success"
        )
        setParent({ historyRow: null, loading: false })
        return
    }

    setParent({ loading: false })
    useNotification(
        "Ops!",
        "Não foi possível reenviar a cobrança, tente novamente em alguns instantes.",
        "danger"
    )
}

const ResendInvoiceComponent = ({ historyRow, setParent }) => {

    const [state, setState] = useState({
        includeManagers: true,
        extraEmails: ""
    })

    return (
        <Dialog fullWidth maxWidth="sm" open={historyRow !== null} >
            <DialogTitle className={'group-flexbox'}>
                <span>Reenviar cobrança</span>
                <Button sx={{ 'minWidth': '2rem', 'padding': '0' }} onClick={() => setParent({ historyRow: null })}>
                    <CloseIcon />
                </Button>
            </DialogTitle>
            <Divider />
            <DialogContent style={{ paddingTop: "2rem" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <FormControlLabel
                        control={<Switch checked={state.includeManagers} onClick={() => setState(prevState => ({ ...prevState, includeManagers: !prevState.includeManagers }))} />}
                        label="Gerentes de conta"
                    />
                    <TextField
                        type="text"
                        label="E-mails adicionais"
                        size='small'
                        value={state.extraEmails}
                        onChange={(event) => setState(prevState => ({ ...prevState, extraEmails: event.target.value }))}
                        fullWidth
                    />
                    <Alert severity="info">E-mails adicionais separados por vírgula.</Alert>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setParent({ historyRow: null })}>Cancelar</Button>
                <Button onClick={() => resendInvoice(historyRow, state, setParent)}>
                    Reenviar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

const CancelNfseComponent = ({ historyRow, setParent, history }) => {
    const [state, setState] = useState({
        captcha: "",
        reasonCode: "2",
        reasonDescription: "Servico nao prestado"
    })

    const isOther = state.reasonCode === "9"
    const descriptionLength = (state.reasonDescription || "").trim().length

    const canSubmit =
        state.captcha?.trim().toLowerCase() === "cancelar" &&
        !!state.reasonCode &&
        (
            !isOther ||
            (
                descriptionLength >= NFSE_CANCEL_DESCRIPTION_MIN &&
                descriptionLength <= NFSE_CANCEL_DESCRIPTION_MAX
            )
        )

    return (
        <Dialog fullWidth maxWidth="sm" open={historyRow !== null}>
            <DialogTitle className={'group-flexbox'}>
                <span>Cancelar nota {historyRow?.nfse_number || ""}</span>
                <Button
                    sx={{ minWidth: '2rem', padding: '0' }}
                    onClick={() => setParent({ cancelNfseRow: null })}
                >
                    <CloseIcon />
                </Button>
            </DialogTitle>
            <Divider />
            <DialogContent style={{ paddingTop: "2rem" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Alert severity="warning">
                        O cancelamento da NFS-e é uma ação irreversível. Selecione o motivo enviado para a Receita e confirme digitando <b>cancelar</b>.
                    </Alert>

                    <TextField
                        select
                        label="Motivo do cancelamento"
                        size="small"
                        value={state.reasonCode}
                        onChange={(event) => {
                            const reasonCode = event.target.value
                            setState(prevState => ({
                                ...prevState,
                                reasonCode,
                                reasonDescription: reasonCode === "9" ? "" : NFSE_CANCEL_REASON_DEFAULT_DESCRIPTION[reasonCode]
                            }))
                        }}
                        fullWidth
                    >
                        {NFSE_CANCEL_REASONS.map(reason => (
                            <MenuItem key={reason.value} value={reason.value}>
                                {reason.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Alert severity="info">
                        {NFSE_CANCEL_REASON_HELP[state.reasonCode]}
                    </Alert>

                    {isOther &&
                        <TextField
                            label="Descrição obrigatória do motivo"
                            size="small"
                            value={state.reasonDescription}
                            onChange={(event) =>
                                setState(prevState => ({
                                    ...prevState,
                                    reasonDescription: event.target.value
                                }))
                            }
                            multiline
                            minRows={3}
                            fullWidth
                            required
                            inputProps={{ maxLength: NFSE_CANCEL_DESCRIPTION_MAX }}
                            error={
                                state.reasonDescription.length > 0 &&
                                (
                                    state.reasonDescription.trim().length < NFSE_CANCEL_DESCRIPTION_MIN ||
                                    state.reasonDescription.trim().length > NFSE_CANCEL_DESCRIPTION_MAX
                                )
                            }
                            helperText={`${descriptionLength}/${NFSE_CANCEL_DESCRIPTION_MAX} caracteres. Mínimo de ${NFSE_CANCEL_DESCRIPTION_MIN}.`}
                        />
                    }

                    <TextField
                        label='Digite "cancelar" para confirmar'
                        size='small'
                        value={state.captcha}
                        onChange={(event) =>
                            setState(prevState => ({
                                ...prevState,
                                captcha: event.target.value
                            }))
                        }
                        fullWidth
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setParent({ cancelNfseRow: null })}>
                    Fechar
                </Button>
                <Button
                    color="error"
                    disabled={!canSubmit}
                    onClick={() => cancelNfse(historyRow, state, setParent, history)}
                >
                    Confirmar cancelamento
                </Button>
            </DialogActions>
        </Dialog>
    )
}

const createNfse = async (row, setParent, history) => {
    setParent({ loading: true })
    // const res = await api.post("api/payment_history/createnfseauto/", {
    //     "valor_servico": Math.floor((row.monthly_value + row.monthly_exceeding)/.95 * 100) / 100,
    //     "descricao": "Licenciamento de uso de programa de computação",
    //     "cnpj_cliente": row.enterprise_cnpj,
    //     "payment_history_id": row.id
    // })

    try {
        const res = await api.post("api/payment_history/createnfseauto/", {
            payment_history_id: row.id
        })

        if (res.status === 200) {
            setParent({
                createNfseRow: null,
                history: history.map(historyRow =>
                    historyRow.id === row.id ? res.data : historyRow
                ),
                loading: false
            })

            useNotification(
                "Sucesso!",
                res.data?.detail || "Solicitação processada com sucesso.",
                "success"
            )
            return
        }

        setParent({ loading: false })
        useNotification(
            "Ops!",
            res.data?.detail || "Não foi possível emitir a nota fiscal.",
            "danger"
        )
    } catch (error) {
        setParent({ loading: false })
        useNotification(
            "Ops!",
            error?.response?.data?.detail || "Não foi possível emitir a nota fiscal.",
            "danger"
        )
    }
}

const cancelNfse = async (row, cancelData, setParent, history) => {
    setParent({ loading: true })

    try {
        const res = await api.post("api/payment_history/cancelnfseauto/", {
            payment_history_id: row.id,
            reason_code: cancelData.reasonCode,
            reason_description: cancelData.reasonDescription
        })

        if (res.status === 200) {
            setParent({
                cancelNfseRow: null,
                history: history.map(historyRow =>
                    historyRow.id === row.id ? { ...historyRow, ...res.data } : historyRow
                ),
                loading: false
            })

            useNotification(
                "Sucesso!",
                res.data?.detail || "Nota fiscal cancelada com sucesso.",
                "success"
            )
            return
        }

        setParent({ loading: false })
        useNotification(
            "Ops!",
            res.data?.detail || "Não foi possível cancelar a nota fiscal.",
            "danger"
        )
    } catch (error) {
        setParent({ loading: false })
        useNotification(
            "Ops!",
            error?.response?.data?.detail || "Não foi possível cancelar a nota fiscal.",
            "danger"
        )
    }
}

const downloadNfse = async (row, setParent) => {
    if (!row.nfse) {
        useNotification(
            "Indisponível!",
            "A nota fiscal não está disponível.",
            "info"
        )
        return
    }

    try {
        setParent({ loading: true })

        const res = await api.get("/api/payment_history/fetch_danfse/", {
            params: { payment_history_id: row.id },
            responseType: "blob"
        })

        const blob = new Blob(
            [res.data],
            { type: res.headers["content-type"] || "application/pdf" }
        )

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.download = `danfse-${row.nfse}.pdf`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
    } catch (error) {
        useNotification(
            "Indisponível!",
            "Não foi possível baixar a DANFSE.",
            "info"
        )
    } finally {
        setParent({ loading: false })
    }
}

const PaymentsComponent = () => {

    const [filterModel, setFilterModel] = useState({ items: [] })
    const [state, setState] = useReducer(lockedReducer, {
        history: [],
        historyRow: null,
        createNfseRow: null,
        cancelNfseRow: null,
        loading: false
    })

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search)
        const value = queryParams.get("enterprise")
        if (value) {
            setFilterModel({
                items: [{
                    columnField: "enterprise_name",
                    operatorValue: "contains",
                    value: value
                }]
            })
        }
    }, [location.search])

    useEffect(() => {
        getPayments(setState)
    }, [])

    return (
        <Box className="payments-container">
            <Loading loading={state.loading} />
            {state.historyRow &&
                <ResendInvoiceComponent
                    historyRow={state.historyRow}
                    setParent={setState}
                    handleClose={() => setState({ historyRow: null })}
                />
            }
            {state.createNfseRow &&
                <ConfirmationDialogCaptcha
                    captcha={'emitir'}
                    description={<Box>Para emitir a nota, digite <b>emitir</b> no campo abaixo.</Box>}
                    alternateYes='Confirmar'
                    alternateNo='Fechar'
                    open={true}
                    title="Emitir nota fiscal"
                    handleClose={() => setState({ createNfseRow: null })}
                    handleSubmit={() => createNfse(state.createNfseRow, setState, state.history)}
                />
            }
            {state.cancelNfseRow &&
                <CancelNfseComponent
                    historyRow={state.cancelNfseRow}
                    history={state.history}
                    setParent={setState}
                />
            }
            <ThemeProvider theme={theme}>
                <DataGridPro
                    rows={state.history}
                    columns={historyColumns(setState)}
                    density={"compact"}
                    pagination
                    paginationMode="client"
                    pageSize={20}
                    rowsPerPageOptions={[20, 50, 100]}
                    disableSelectionOnClick
                    components={{ Toolbar: CustomToolbar }}
                    filterModel={filterModel}
                    onFilterModelChange={(newModel) => setFilterModel(newModel)}
                />
            </ThemeProvider>
        </Box>
    )
}


export default PaymentsComponent
